import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Plus, X, Check, Shield, ExternalLink, AlertTriangle, Merge, Image, Calendar, Heart, Users, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LinkedAccountsSection = ({ user, onAccountsMerged }) => {
  const [linkedEmails, setLinkedEmails] = useState(user?.linked_emails || []);
  const [linkedPhones, setLinkedPhones] = useState(user?.linked_phones || []);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [beehiivConsent, setBeehiivConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Merge dialog state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [conflictAccount, setConflictAccount] = useState(null);
  const [conflictType, setConflictType] = useState(null); // 'email' or 'phone'
  const [conflictValue, setConflictValue] = useState(null);
  const [mergeOptions, setMergeOptions] = useState({
    inspo: true,
    inspo_ids: ["all"],
    defaults: true,
    default_categories: [],
    experiences: true,
    rsvps: true
  });
  const [merging, setMerging] = useState(false);
  const [showInspoDetails, setShowInspoDetails] = useState(false);
  const [selectedInspoIds, setSelectedInspoIds] = useState(new Set());

  // Check for conflict when adding email/phone
  const checkForConflict = async (email = null, phone = null) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/user/check-account-conflict`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ email, phone })
      });
      
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      return { has_conflict: false };
    } catch (err) {
      console.error("Conflict check failed:", err);
      return { has_conflict: false };
    }
  };

  const handleGoogleLink = async () => {
    if (!beehiivConsent) {
      toast.error("Please agree to the terms first");
      return;
    }
    // Store consent and redirect to Google OAuth
    localStorage.setItem('link_email_consent', 'true');
    localStorage.setItem('link_email_redirect', '/me');
    const redirectUrl = window.location.origin + '/auth/callback?action=link_email';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendPhoneOtp = async () => {
    if (!newPhone || newPhone.length < 10) {
      toast.error("Enter a valid phone number");
      return;
    }
    
    setLoading(true);
    
    // First check if there's a conflict
    const conflict = await checkForConflict(null, newPhone);
    
    if (conflict.has_conflict) {
      // Show merge dialog
      setConflictAccount(conflict.existing_account);
      setConflictType('phone');
      setConflictValue(newPhone);
      setMergeOptions({
        inspo: true,
        inspo_ids: ["all"],
        defaults: true,
        default_categories: conflict.existing_account.default_categories || [],
        experiences: true,
        rsvps: true
      });
      setSelectedInspoIds(new Set(conflict.existing_account.inspo_sample?.map(i => i.inspiration_id) || []));
      setShowMergeDialog(true);
      setLoading(false);
      return;
    }
    
    // No conflict - proceed with OTP
    try {
      const res = await fetch(`${API}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: newPhone })
      });
      
      if (res.ok) {
        setOtpSent(true);
        toast.success("Verification code sent! Use 123456 for testing.");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Failed to send OTP");
    }
    setLoading(false);
  };

  const handleLinkPhone = async () => {
    if (!phoneOtp) {
      toast.error("Enter the verification code");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/user/link-phone`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ phone: newPhone, otp: phoneOtp })
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkedPhones(data.linked_phones);
        setShowAddPhone(false);
        setNewPhone("");
        setPhoneOtp("");
        setOtpSent(false);
        toast.success("Phone linked successfully!");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to link phone");
      }
    } catch (err) {
      toast.error("Failed to link phone");
    }
    setLoading(false);
  };

  const handleUnlinkEmail = async (email) => {
    if (email === user?.email) {
      toast.error("Cannot unlink primary email");
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/user/unlink-email/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkedEmails(data.linked_emails);
        toast.success("Email unlinked");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to unlink");
      }
    } catch (err) {
      toast.error("Failed to unlink");
    }
  };

  const handleUnlinkPhone = async (phone) => {
    if (phone === user?.phone) {
      toast.error("Cannot unlink primary phone");
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/user/unlink-phone/${phone}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkedPhones(data.linked_phones);
        toast.success("Phone unlinked");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to unlink");
      }
    } catch (err) {
      toast.error("Failed to unlink");
    }
  };

  // Execute the merge
  const handleMergeAccounts = async () => {
    if (!conflictAccount) return;
    
    setMerging(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // Build merge options
      const finalOptions = {
        inspo: mergeOptions.inspo,
        inspo_ids: mergeOptions.inspo && !mergeOptions.inspo_ids.includes("all") 
          ? Array.from(selectedInspoIds) 
          : ["all"],
        defaults: mergeOptions.defaults,
        default_categories: mergeOptions.default_categories,
        experiences: mergeOptions.experiences,
        rsvps: mergeOptions.rsvps
      };
      
      const res = await fetch(`${API}/user/merge-account`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          source_user_id: conflictAccount.user_id,
          merge_options: finalOptions,
          verified_phone: conflictType === 'phone' ? conflictValue : null,
          verified_email: conflictType === 'email' ? conflictValue : null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkedEmails(data.linked_emails || []);
        setLinkedPhones(data.linked_phones || []);
        
        toast.success(`Account merged! ${Object.entries(data.items_merged || {}).map(([k, v]) => `${v} ${k}`).join(', ')}`);
        
        setShowMergeDialog(false);
        setConflictAccount(null);
        setShowAddPhone(false);
        setShowAddEmail(false);
        setNewPhone("");
        setPhoneOtp("");
        setOtpSent(false);
        
        // Callback to refresh parent data
        if (onAccountsMerged) {
          onAccountsMerged();
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to merge accounts");
      }
    } catch (err) {
      toast.error("Failed to merge accounts");
    }
    setMerging(false);
  };

  // Toggle inspo selection
  const toggleInspoSelection = (inspoId) => {
    const newSelected = new Set(selectedInspoIds);
    if (newSelected.has(inspoId)) {
      newSelected.delete(inspoId);
    } else {
      newSelected.add(inspoId);
    }
    setSelectedInspoIds(newSelected);
    
    // Update merge options
    if (newSelected.size === 0) {
      setMergeOptions({...mergeOptions, inspo: false});
    } else {
      setMergeOptions({...mergeOptions, inspo: true, inspo_ids: Array.from(newSelected)});
    }
  };

  // Select all inspo
  const selectAllInspo = () => {
    const allIds = new Set(conflictAccount?.inspo_sample?.map(i => i.inspiration_id) || []);
    setSelectedInspoIds(allIds);
    setMergeOptions({...mergeOptions, inspo: true, inspo_ids: ["all"]});
  };

  // Deselect all inspo
  const deselectAllInspo = () => {
    setSelectedInspoIds(new Set());
    setMergeOptions({...mergeOptions, inspo: false, inspo_ids: []});
  };

  // Toggle default category
  const toggleDefaultCategory = (cat) => {
    const newCats = mergeOptions.default_categories.includes(cat)
      ? mergeOptions.default_categories.filter(c => c !== cat)
      : [...mergeOptions.default_categories, cat];
    setMergeOptions({...mergeOptions, default_categories: newCats, defaults: newCats.length > 0});
  };

  return (
    <div className="space-y-4">
      {/* Linked Emails */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Linked Emails ({linkedEmails.length}/5)
            </CardTitle>
            {linkedEmails.length < 5 && !showAddEmail && (
              <Button size="sm" variant="ghost" onClick={() => setShowAddEmail(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* Primary email */}
          {user?.email && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{user.email}</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Primary</span>
              </div>
            </div>
          )}
          
          {linkedEmails.filter(e => e !== user?.email).map((email, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{email}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleUnlinkEmail(email)}>
                <X className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
          ))}

          {showAddEmail && (
            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <p className="text-sm font-medium">Link Gmail Account</p>
              
              <div className="flex items-start gap-2">
                <Checkbox 
                  id="beehiiv-consent" 
                  checked={beehiivConsent} 
                  onCheckedChange={setBeehiivConsent}
                />
                <label htmlFor="beehiiv-consent" className="text-xs text-gray-600 leading-tight">
                  I agree to receive updates and accept the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Terms</a>,{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Privacy</a>,{" "}
                  <a href="https://www.beehiiv.com/tou" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Beehiiv TOU</a> &{" "}
                  <a href="https://www.beehiiv.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Beehiiv Privacy</a>
                </label>
              </div>

              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={handleGoogleLink}
                  disabled={!beehiivConsent}
                  className="flex-1"
                >
                  <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 mr-2" />
                  Sign in with Google
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddEmail(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Linked Phones */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Linked Phones ({linkedPhones.length}/3)
            </CardTitle>
            {linkedPhones.length < 3 && !showAddPhone && (
              <Button size="sm" variant="ghost" onClick={() => setShowAddPhone(true)} data-testid="add-phone-btn">
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* Primary phone */}
          {user?.phone && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{user.phone}</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Primary</span>
              </div>
            </div>
          )}
          
          {linkedPhones.filter(p => p !== user?.phone).map((phone, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{phone}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleUnlinkPhone(phone)}>
                <X className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
          ))}

          {showAddPhone && (
            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <p className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Link Phone (2FA Required)
              </p>
              
              {!otpSent ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Phone number"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="flex-1"
                    data-testid="new-phone-input"
                  />
                  <Button size="sm" onClick={handleSendPhoneOtp} disabled={loading} data-testid="send-otp-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Code"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Enter 6-digit code"
                    value={phoneOtp}
                    onChange={(e) => setPhoneOtp(e.target.value)}
                    maxLength={6}
                    data-testid="otp-input"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleLinkPhone} disabled={loading} className="flex-1" data-testid="verify-link-btn">
                      <Check className="w-4 h-4 mr-2" />
                      Verify & Link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setShowAddPhone(false);
                      setOtpSent(false);
                      setNewPhone("");
                      setPhoneOtp("");
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merge Account Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-amber-500" />
              Account Already Exists
            </DialogTitle>
            <DialogDescription>
              This {conflictType} is linked to another account. Would you like to merge it into your current account?
            </DialogDescription>
          </DialogHeader>
          
          {conflictAccount && (
            <div className="space-y-4 py-2">
              {/* Existing Account Info */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  {conflictAccount.profile_photo_url || conflictAccount.picture ? (
                    <img 
                      src={conflictAccount.profile_photo_url || conflictAccount.picture} 
                      alt="" 
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <Users className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium">{conflictAccount.name || "Unknown"}</h4>
                    <p className="text-xs text-gray-500">
                      {conflictAccount.email && <span className="mr-2">{conflictAccount.email}</span>}
                      {conflictAccount.phone && <span>{conflictAccount.phone}</span>}
                    </p>
                  </div>
                </div>
                
                {/* Account Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-lg font-semibold">{conflictAccount.inspo_count || 0}</div>
                    <div className="text-xs text-gray-500">Inspo</div>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-lg font-semibold">{conflictAccount.experiences_count || 0}</div>
                    <div className="text-xs text-gray-500">Experiences</div>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-lg font-semibold">{conflictAccount.default_categories?.length || 0}</div>
                    <div className="text-xs text-gray-500">Defaults</div>
                  </div>
                </div>
              </div>
              
              {/* Merge Options */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm">What would you like to bring over?</h4>
                
                {/* Inspo Section */}
                {conflictAccount.inspo_count > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <button 
                      className="w-full p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                      onClick={() => setShowInspoDetails(!showInspoDetails)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={mergeOptions.inspo}
                          onCheckedChange={(checked) => {
                            if (checked) selectAllInspo();
                            else deselectAllInspo();
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Image className="w-4 h-4 text-pink-500" />
                        <span className="text-sm font-medium">Inspiration Photos</span>
                        <Badge variant="secondary" className="text-xs">
                          {mergeOptions.inspo_ids?.includes("all") ? conflictAccount.inspo_count : selectedInspoIds.size} selected
                        </Badge>
                      </div>
                      {showInspoDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {showInspoDetails && (
                      <div className="p-3 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500">Select specific photos or bring all</span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" className="text-xs h-6" onClick={selectAllInspo}>
                              Select All
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs h-6" onClick={deselectAllInspo}>
                              None
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {conflictAccount.inspo_sample?.map((inspo) => (
                            <button
                              key={inspo.inspiration_id}
                              onClick={() => toggleInspoSelection(inspo.inspiration_id)}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                selectedInspoIds.has(inspo.inspiration_id) 
                                  ? 'border-pink-500 ring-2 ring-pink-200' 
                                  : 'border-transparent hover:border-gray-300'
                              }`}
                            >
                              {inspo.image_url ? (
                                <img 
                                  src={inspo.image_url} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                  <Image className="w-6 h-6 text-gray-300" />
                                </div>
                              )}
                              {selectedInspoIds.has(inspo.inspiration_id) && (
                                <div className="absolute inset-0 bg-pink-500/20 flex items-center justify-center">
                                  <Check className="w-6 h-6 text-white drop-shadow-lg" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                        {conflictAccount.inspo_count > 6 && (
                          <p className="text-xs text-gray-400 mt-2 text-center">
                            +{conflictAccount.inspo_count - 6} more photos will be included if "Select All"
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Defaults Section */}
                {conflictAccount.default_categories?.length > 0 && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Checkbox 
                        checked={mergeOptions.defaults && mergeOptions.default_categories.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setMergeOptions({
                              ...mergeOptions, 
                              defaults: true, 
                              default_categories: conflictAccount.default_categories
                            });
                          } else {
                            setMergeOptions({...mergeOptions, defaults: false, default_categories: []});
                          }
                        }}
                      />
                      <Heart className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium">Default Preferences</span>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      {conflictAccount.default_categories.map((cat) => (
                        <Badge 
                          key={cat}
                          variant={mergeOptions.default_categories.includes(cat) ? "default" : "outline"}
                          className="cursor-pointer capitalize"
                          onClick={() => toggleDefaultCategory(cat)}
                        >
                          {cat}
                          {mergeOptions.default_categories.includes(cat) && (
                            <Check className="w-3 h-3 ml-1" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Experiences */}
                {conflictAccount.experiences_count > 0 && (
                  <div className="p-3 border rounded-lg flex items-center gap-3">
                    <Checkbox 
                      checked={mergeOptions.experiences}
                      onCheckedChange={(checked) => setMergeOptions({...mergeOptions, experiences: checked})}
                    />
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Experiences ({conflictAccount.experiences_count})</span>
                  </div>
                )}
                
                {/* RSVPs */}
                {conflictAccount.rsvp_count > 0 && (
                  <div className="p-3 border rounded-lg flex items-center gap-3">
                    <Checkbox 
                      checked={mergeOptions.rsvps}
                      onCheckedChange={(checked) => setMergeOptions({...mergeOptions, rsvps: checked})}
                    />
                    <Users className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">Event RSVPs ({conflictAccount.rsvp_count})</span>
                  </div>
                )}
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700">
                  The other account will be deactivated after merge. This action cannot be undone.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMergeAccounts} 
              disabled={merging}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {merging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Merge className="w-4 h-4 mr-2" />
                  Merge Accounts
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LinkedAccountsSection;
